import React, { useMemo, useState } from 'react';
import { useMoney } from '../hooks/useMoney';
import { useRole } from '../hooks/useRole';
import { FormInput, FormSelect, FormTextarea } from './FormField';
import { CurrencyInput } from './ui/CurrencyInput';
import ProcSection from './procurement/ProcSection';
import { useFieldErrors, useFormSaveEvent, useClearErrorsOnEdit } from './forms/_shared';
import { validationError } from '../utils/procurementErrors';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import LineItemsEditor, { LineItem, LineItemFieldConfig } from './ui/LineItemsEditor';
import { CURRENCY_OPTIONS, currencySymbol } from '../utils/currency';
import type { ShippingAgent, ShippingAgentOffice, ShippingMode } from '../types/Procurement';
import type { CreateShippingAgentInput } from '../services/ShippingAgentService';

/**
 * Create/edit form for a single shipping agent. It owns a local draft and only
 * hands the caller a payload on submit, so cancelling never touches the list.
 *
 * Money is a decimal string with the agent's own currency beside it. Someone
 * who may not see money (a contractor) gets no money fields, and their payload
 * leaves money out entirely, so saving never writes the nulls they read back.
 */

const MODE_OPTIONS: { value: ShippingMode; label: string }[] = [
  { value: 'sea', label: 'Sea' },
  { value: 'air', label: 'Air' },
  { value: 'road', label: 'Road' },
  { value: 'rail', label: 'Rail' },
  { value: 'bike', label: 'Bike' },
];

const officeFields = (showMoney: boolean): LineItemFieldConfig[] => [
  { key: 'label', type: 'text', placeholder: 'Office label', header: 'Label', width: 140 },
  { key: 'address', type: 'text', placeholder: 'Address', header: 'Address' },
  { key: 'phone', type: 'text', placeholder: 'Phone numbers, comma separated', header: 'Phone', width: 200 },
  ...(showMoney ? [{ key: 'pickup_fee', type: 'currency' as const, placeholder: '0', header: 'Pickup fee', width: 125 }] : []),
];

function officesToLineItems(offices: ShippingAgentOffice[]): LineItem[] {
  return offices.map((o, idx) => ({
    id: `office-${idx}`,
    values: {
      label: o.label ?? '',
      address: o.address ?? '',
      phone: (o.phone ?? []).join(', '),
      pickup_fee: o.pickup_fee ?? '',
    },
  }));
}

function lineItemsToOffices(items: LineItem[], showMoney: boolean): ShippingAgentOffice[] {
  return items.map(li => ({
    label: li.values.label ?? '',
    address: li.values.address ?? '',
    phone: (li.values.phone ?? '').split(',').map(p => p.trim()).filter(Boolean),
    pickup_fee: showMoney ? (li.values.pickup_fee?.trim() || null) : null,
  }));
}

export interface ShippingAgentDraft {
  slug: string;
  name: string;
  shipping_mode: ShippingMode;
  active: boolean;
  currency: string;
  rate_per_cbm: string;
  rate_per_kg: string;
  packing_fee: string;
  packing_fee_threshold_cbm: string;
  website: string;
  email: string;
  phone: string;
  tracking_url: string;
  notes: string;
  offices: ShippingAgentOffice[];
}

const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

export function agentToDraft(agent?: ShippingAgent | null, defaultCurrency = ''): ShippingAgentDraft {
  return {
    slug: agent?.slug ?? '',
    name: agent?.name ?? '',
    shipping_mode: agent?.shipping_mode ?? 'sea',
    active: agent?.active ?? true,
    currency: agent?.currency ?? defaultCurrency,
    rate_per_cbm: s(agent?.rate_per_cbm),
    rate_per_kg: s(agent?.rate_per_kg),
    packing_fee: s(agent?.packing_fee),
    packing_fee_threshold_cbm: s(agent?.packing_fee_threshold_cbm),
    website: agent?.website ?? '',
    email: agent?.email ?? '',
    phone: agent?.phone ?? '',
    tracking_url: agent?.tracking_url ?? '',
    notes: agent?.notes ?? '',
    offices: agent?.offices ?? [],
  };
}

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());
const hasAmount = (d: ShippingAgentDraft): boolean =>
  [d.rate_per_cbm, d.rate_per_kg, d.packing_fee].some(v => v.trim() !== '') || d.offices.some(o => (o.pickup_fee ?? '').trim() !== '');

/** Field-level problems found before sending, keyed by the API's field name. An amount with no currency beside it is a 400 on the server. */
export function validateAgentFields(draft: ShippingAgentDraft, canViewFinancials: boolean): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!draft.name.trim()) errors.name = 'Give the agent a name.';
  if (!draft.slug.trim()) errors.slug = 'Give the agent a slug, for example colala-sea.';
  if (canViewFinancials && hasAmount(draft) && !draft.currency.trim()) errors.currency = 'Choose the currency these amounts are in.';
  return errors;
}

/** The first problem to show before sending, or null. */
export function validateAgentDraft(draft: ShippingAgentDraft, canViewFinancials: boolean): string | null {
  return Object.values(validateAgentFields(draft, canViewFinancials))[0] ?? null;
}

export function draftToPayload(draft: ShippingAgentDraft, canViewFinancials = true): CreateShippingAgentInput {
  const base: CreateShippingAgentInput = {
    slug: draft.slug.trim(),
    name: draft.name.trim(),
    shipping_mode: draft.shipping_mode,
    active: draft.active,
    packing_fee_threshold_cbm: orNull(draft.packing_fee_threshold_cbm),
    website: orNull(draft.website),
    email: orNull(draft.email),
    phone: orNull(draft.phone),
    tracking_url: orNull(draft.tracking_url),
    notes: orNull(draft.notes),
  };
  if (!canViewFinancials) return base;
  return {
    ...base,
    currency: orNull(draft.currency),
    rate_per_cbm: orNull(draft.rate_per_cbm),
    rate_per_kg: orNull(draft.rate_per_kg),
    packing_fee: orNull(draft.packing_fee),
    offices: draft.offices,
  };
}

interface ShippingAgentFormProps {
  agent?: ShippingAgent | null;
  /** Sends the payload. It must throw when the save fails, so the shared save hook can show the reason on the fields. */
  onSubmit: (payload: CreateShippingAgentInput) => Promise<void>;
  /** Read-only for a viewer. */
  readOnly?: boolean;
}

/** The window event the breadcrumb bar's Save sends (AgentDetailBar). */
export const AGENT_SAVE_EVENT = 'shipping-agent-save';

export default function ShippingAgentForm({ agent, onSubmit, readOnly }: ShippingAgentFormProps) {
  const money = useMoney();
  const { canViewFinancials } = useRole();
  const [draft, setDraft] = useState<ShippingAgentDraft>(() => agentToDraft(agent, money.currency));
  const fieldErrors = useFieldErrors();
  const officeItems = useMemo(() => officesToLineItems(draft.offices), [draft.offices]);
  const symbol = currencySymbol(draft.currency);
  const err = fieldErrors.errorFor;

  const set = <K extends keyof ShippingAgentDraft>(key: K, value: ShippingAgentDraft[K]) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  // Save (from the breadcrumb bar, or Enter in a field) validates, then sends; a failure throws to the shared hook.
  const save = async () => {
    if (readOnly) return;
    const found = validateAgentFields(draft, canViewFinancials);
    if (Object.keys(found).length > 0) throw validationError(found);
    await onSubmit(draftToPayload(draft, canViewFinancials));
  };
  useFormSaveEvent(save, { event: AGENT_SAVE_EVENT, fieldErrors, fallback: 'Failed to save the agent.' });
  useClearErrorsOnEdit(draft as unknown as Record<string, unknown>, fieldErrors);
  const initial = useMemo(() => JSON.stringify(agentToDraft(agent, money.currency)), [agent, money.currency]);
  useUnsavedChangesGuard(!readOnly && JSON.stringify(draft) !== initial);

  const handleEnter = (e: React.FormEvent) => {
    e.preventDefault();
    window.dispatchEvent(new Event(AGENT_SAVE_EVENT));
  };

  return (
    <form onSubmit={handleEnter} noValidate>
      <fieldset disabled={readOnly} className="proc-fieldset">
        <ProcSection title="Identity" ariaLabel="Agent identity">
          <div className="form-row">
            <FormInput label="Name" required value={draft.name} error={err('name')} onChange={e => set('name', e.target.value)} />
            <FormInput label="Slug" required help="Unique identifier, e.g. colala-sea" value={draft.slug} error={err('slug')} onChange={e => set('slug', e.target.value)} />
          </div>
          <div className="form-row">
            <FormSelect label="Shipping mode" value={draft.shipping_mode} error={err('shipping_mode')} options={MODE_OPTIONS} onChange={e => set('shipping_mode', e.target.value as ShippingMode)} />
            <FormInput label="Packing fee threshold (CBM)" inputMode="decimal" value={draft.packing_fee_threshold_cbm} error={err('packing_fee_threshold_cbm')} onChange={e => set('packing_fee_threshold_cbm', e.target.value)} />
          </div>
        </ProcSection>

        {canViewFinancials && (
          <ProcSection title="Default rates" ariaLabel="Default rates">
            <div className="form-row">
              <FormSelect
                label="Currency of the amounts" value={draft.currency} error={err('currency')} onChange={e => set('currency', e.target.value)}
                options={[{ value: '', label: 'Choose a currency' }, ...CURRENCY_OPTIONS]}
              />
              {draft.shipping_mode === 'air' ? (
                <CurrencyInput label={`Rate per kg${draft.currency ? ` (${draft.currency})` : ''}`} symbol={symbol} value={draft.rate_per_kg} error={err('rate_per_kg')} onChange={e => set('rate_per_kg', e.target.value)} help="Used for air freight quotes" />
              ) : (
                <CurrencyInput label={`Rate per CBM${draft.currency ? ` (${draft.currency})` : ''}`} symbol={symbol} value={draft.rate_per_cbm} error={err('rate_per_cbm')} onChange={e => set('rate_per_cbm', e.target.value)} help="Used for sea, road, rail and bike freight quotes" />
              )}
              <CurrencyInput label={`Packing fee${draft.currency ? ` (${draft.currency})` : ''}`} symbol={symbol} value={draft.packing_fee} error={err('packing_fee')} onChange={e => set('packing_fee', e.target.value)} />
            </div>
          </ProcSection>
        )}

        <ProcSection title="Contact" ariaLabel="Agent contact">
          <div className="form-row">
            <FormInput label="Website" type="url" value={draft.website} error={err('website')} onChange={e => set('website', e.target.value)} />
            <FormInput label="Email" type="email" value={draft.email} error={err('email')} onChange={e => set('email', e.target.value)} />
            <FormInput label="Phone" value={draft.phone} error={err('phone')} onChange={e => set('phone', e.target.value)} />
          </div>
          <div className="form-row">
            <FormInput
              label="Tracking URL" type="url"
              help="A placeholder for the tracking number, e.g. https://track.example.com/{tracking_no}"
              value={draft.tracking_url} error={err('tracking_url')} onChange={e => set('tracking_url', e.target.value)}
            />
          </div>
        </ProcSection>

        <ProcSection title="Notes" ariaLabel="Agent notes">
          <FormTextarea label="Notes" value={draft.notes} error={err('notes')} onChange={e => set('notes', e.target.value)} />
        </ProcSection>

        <LineItemsEditor
          items={officeItems}
          fields={officeFields(canViewFinancials)}
          onChange={items => set('offices', lineItemsToOffices(items, canViewFinancials))}
          addLabel="Add office"
          section={{ title: 'Offices' }}
        />
      </fieldset>
    </form>
  );
}
